import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  MAX_CUSTOM_SHORTENERS,
  MAX_IGNORED_TERMS,
  MAX_TERM_LENGTH,
  normalizeDomain,
  PROVIDER_LABELS,
  type ForwardingSettings,
  type UserTemplate,
} from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { buildPreviewHtml, forwardingErrorMessage, normalizeTerm } from "@/lib/forwarding";
import { useSaveForwardingSettings } from "@/lib/hooks/useForwarding";
import { Sheet, SheetSection } from "./Sheet";
import { ChipListInput } from "./ChipListInput";
import { MessagePreview } from "./MessagePreview";
import { ValueFilterFields } from "./ValueFilterFields";
import { TemplateRefSelect } from "./TemplateRefSelect";
import { InlineError, Segmented, Toggle, selectClassName } from "./controls";

interface GlobalDefaultsSheetProps {
  readonly open: boolean;
  readonly settings: ForwardingSettings;
  readonly templates: readonly UserTemplate[];
  readonly onClose: () => void;
}

const DEDUP_OPTIONS = [
  { value: 0, label: "Desligado" },
  { value: 60, label: "1 hora" },
  { value: 360, label: "6 horas" },
  { value: 1440, label: "24 horas" },
  { value: 4320, label: "3 dias" },
];

const PREVIEW_PROVIDERS = ["shopee", "amazon", "mercadolivre", "magalu", "aliexpress", "shein"];

function AppearanceSection({ draft, templates, set }: SectionArgs): JSX.Element {
  return (
    <SheetSection
      title="Aparência do texto"
      hint="Use o seu template no lugar do texto que o criador da oferta escreveu."
    >
      <Toggle
        id="gd-use-template"
        checked={draft.useTemplate}
        onChange={(useTemplate) => set({ useTemplate })}
        label="Usar meu template"
        hint="Desligado, a mensagem segue com o texto original e só o link é trocado."
      />
      {draft.useTemplate && (
        <div className="space-y-1.5">
          <label htmlFor="gd-template" className="text-xs font-medium text-foreground">
            Template padrão
          </label>
          <TemplateRefSelect
            id="gd-template"
            value={draft.defaultTemplateRef}
            templates={templates}
            emptyLabel="Padrão do LucraLink"
            onChange={(defaultTemplateRef) => set({ defaultTemplateRef })}
          />
          <p className="text-[11px] text-muted-foreground">
            Template por loja e edição em{" "}
            <Link to="/app/settings/personalizacao" className="text-primary hover:underline">
              Personalização
            </Link>
            .
          </p>
        </div>
      )}
    </SheetSection>
  );
}

interface SectionArgs {
  readonly draft: ForwardingSettings;
  readonly templates: readonly UserTemplate[];
  readonly set: (patch: Partial<ForwardingSettings>) => void;
}

function LinkSection({ draft, set }: SectionArgs): JSX.Element {
  return (
    <SheetSection
      title="Link enviado"
      hint="Amazon, Mercado Livre e Magalu sempre vão com o link de afiliado direto, por regra das lojas."
    >
      <Segmented
        label="Estilo do link"
        value={draft.linkStyle}
        onChange={(linkStyle) => set({ linkStyle })}
        options={[
          { value: "short", label: "Link curto LucraLink" },
          { value: "direct", label: "Afiliado direto" },
        ]}
      />
    </SheetSection>
  );
}

function PreviewSection({ draft, templates }: SectionArgs): JSX.Element {
  const [provider, setProvider] = useState("shopee");
  const html = buildPreviewHtml(draft, templates, provider);
  return (
    <SheetSection
      title="Prévia da mensagem"
      hint="Atualiza enquanto você edita, com o mesmo motor que o bot usa."
    >
      <select
        aria-label="Loja da prévia"
        className={selectClassName}
        value={provider}
        onChange={(e) => setProvider(e.target.value)}
      >
        {PREVIEW_PROVIDERS.map((code) => (
          <option key={code} value={code}>
            {PROVIDER_LABELS[code] ?? code}
          </option>
        ))}
      </select>
      <div className="grid gap-3 rounded-xl border border-dashed border-border bg-[hsl(var(--surface)/0.5)] p-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Telegram
          </p>
          <MessagePreview html={html} platform="telegram" />
        </div>
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            WhatsApp
          </p>
          <MessagePreview html={html} platform="whatsapp" />
        </div>
      </div>
    </SheetSection>
  );
}

function FiltersSection({ draft, set }: SectionArgs): JSX.Element {
  return (
    <>
      <SheetSection
        title="Ignorar mensagens com este conteúdo"
        hint="A linha que contém o termo é apagada. Se não sobrar link válido, a mensagem é descartada. Não diferencia maiúsculas."
      >
        <ChipListInput
          id="gd-terms"
          label="Termos"
          values={draft.ignoredTerms}
          max={MAX_IGNORED_TERMS}
          placeholder="ex.: frete grátis, grupo VIP"
          normalize={normalizeTerm}
          errorText={`Cada termo pode ter até ${MAX_TERM_LENGTH} caracteres.`}
          onChange={(ignoredTerms) => set({ ignoredTerms })}
        />
      </SheetSection>
      <SheetSection
        title="Encurtadores personalizados"
        hint="Links desses domínios (e subdomínios) são abertos até a loja e convertidos com a sua tag."
      >
        <ChipListInput
          id="gd-shorteners"
          label="Domínios"
          values={draft.customShorteners}
          max={MAX_CUSTOM_SHORTENERS}
          placeholder="ex.: meulink.co"
          normalize={normalizeDomain}
          errorText="Informe só o domínio, como meulink.co (sem caminho, porta ou IP)."
          onChange={(customShorteners) => set({ customShorteners })}
        />
      </SheetSection>
      <SheetSection title="Filtro por valor">
        <ValueFilterFields
          idPrefix="gd-vf"
          value={draft.valueFilter}
          onChange={(valueFilter) => set({ valueFilter })}
        />
      </SheetSection>
      <DedupSection draft={draft} set={set} />
    </>
  );
}

function DedupSection({ draft, set }: Omit<SectionArgs, "templates">): JSX.Element {
  return (
    <SheetSection
      title="Anti-duplicação"
      hint="A mesma oferta vinda de origens diferentes é enviada uma vez dentro deste período."
    >
      <select
        aria-label="Janela de anti-duplicação"
        className={selectClassName}
        value={draft.dedupWindowMinutes}
        onChange={(e) => set({ dedupWindowMinutes: Number(e.target.value) })}
      >
        {DEDUP_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </SheetSection>
  );
}

/** Sheet "Padrão global": aparência, link, prévia, filtros e anti-duplicação. */
export function GlobalDefaultsSheet({
  open,
  settings,
  templates,
  onClose,
}: GlobalDefaultsSheetProps): JSX.Element | null {
  const [draft, setDraft] = useState(settings);
  const save = useSaveForwardingSettings();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDraft(settings);
      setError(null);
    }
  }, [open, settings]);

  const set = (patch: Partial<ForwardingSettings>): void => setDraft((d) => ({ ...d, ...patch }));
  const onSave = async (): Promise<void> => {
    try {
      await save.mutateAsync(draft);
      onClose();
    } catch (err) {
      setError(forwardingErrorMessage(err));
    }
  };
  const args: SectionArgs = { draft, templates, set };

  return (
    <Sheet
      open={open}
      title="Padrão global"
      description="Vale para todas as origens. Cada canal pode sobrescrever nos ajustes dele."
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => void onSave()} disabled={save.isPending}>
            {save.isPending ? "Salvando..." : "Salvar padrão"}
          </Button>
        </div>
      }
    >
      <InlineError message={error} />
      <AppearanceSection {...args} />
      <LinkSection {...args} />
      <PreviewSection {...args} />
      <FiltersSection {...args} />
    </Sheet>
  );
}
